import { translateUi } from "./i18n.ts";
type Identity = { sessionId: string; userId: string };
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
type TransportOptions = { proxy: string; direct: string; fetch: typeof fetch; storage?: Storage; directFirst?: boolean; onOriginChange?: (origin: string) => void };
const storageKey = "edubgt_direct_session";
const choiceKey = "edubgt_api_transport";

function identity(value: unknown): Identity | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Partial<Identity>;
  return typeof row.sessionId === "string" && typeof row.userId === "string"
    ? { sessionId: row.sessionId, userId: row.userId } : null;
}
function sameSession(a: Identity | null, b: Identity | null) {
  return !!a && !!b && a.sessionId === b.sessionId && a.userId === b.userId;
}
function unavailableResponse() {
  return new Response(JSON.stringify({ error: translateUi("Lidhja me shërbimin nuk u verifikua. Provo përsëri.", "The service connection could not be verified. Try again.") }), { status: 503, headers: { "Content-Type": "application/json" } });
}

export function createSessionTransport(options: TransportOptions) {
  let directSession: Identity | null = null;
  try { directSession = identity(JSON.parse(options.storage?.getItem(storageKey) || "null")); } catch { /* Storage may be disabled. */ }
  if (options.direct === options.proxy) directSession = null;
  let mode: "direct" | "proxy" | "legacy" = "direct";
  let legacyRecovery = false;
  try {
    if (options.storage?.getItem(choiceKey) === "proxy") mode = "proxy";
    legacyRecovery = !options.storage?.getItem(choiceKey) && !!options.storage?.getItem("edubgt_session_expires_at");
  } catch { /* Storage may be disabled. */ }
  function choose(value: typeof mode) {
    mode = value;
    options.onOriginChange?.(value === "direct" ? options.direct : options.proxy);
    try {
      if (value !== "legacy") options.storage?.setItem(choiceKey, value);
    } catch { /* Keep the choice in memory. */ }
  }
  if (options.directFirst) options.onOriginChange?.(mode === "direct" ? options.direct : options.proxy);
  let generation = 0;
  let attempted = false;
  let pending: Promise<void> | null = null;

  function select(value: Identity | null) {
    directSession = value;
    try {
      if (value) options.storage?.setItem(storageKey, JSON.stringify(value));
      else options.storage?.removeItem(storageKey);
    } catch { /* The current tab can still use its in-memory choice. */ }
  }
  function reset() {
    generation++;
    select(null);
    attempted = false;
    legacyRecovery = false;
    if (options.directFirst) choose("direct");
  }
  function originFor(path: string) {
    const authPath = path.split("?")[0];
    if (["/api/auth/lookup", "/api/auth/config", "/api/public/settings"].includes(authPath)) return options.direct;
    if (options.directFirst) return mode === "direct" ? options.direct : options.proxy;
    const sessionPaths = ["/api/auth/me", "/api/auth/session/heartbeat", "/api/auth/logout"];
    if (authPath.startsWith("/api/auth/") && !sessionPaths.includes(authPath)) return options.proxy;
    return directSession ? options.direct : options.proxy;
  }
  async function send(origin: string, path: string, init: RequestInit) {
    return options.fetch(`${origin}${path}`, { ...init, credentials: "include" });
  }
  async function enable(userId: string) {
    if (directSession || attempted || options.direct === options.proxy) return;
    if (pending) return pending;
    attempted = true;
    const startGeneration = generation;
    pending = (async () => {
      if (options.directFirst && mode === "proxy") return;
      if (options.directFirst && mode === "direct") {
        const verified = await send(options.direct, "/api/auth/session/transport", { signal: AbortSignal.timeout(15_000) });
        const actual = verified.ok ? identity(await verified.json()) : null;
        if (actual?.userId === userId && startGeneration === generation) select(actual);
        return;
      }
      const headers = { "Content-Type": "application/json", "X-EDUBGT-Request": "1", Accept: "application/json" };
      // Hosting ingress can delay this optional background request; it must not consume the Worker's timeout.
      const grant = await send(options.proxy, "/api/auth/session/direct-code", { method: "POST", headers, signal: AbortSignal.timeout(45_000) });
      if (!grant.ok) return;
      const payload = await grant.json() as Identity & { code?: string };
      const expected = identity(payload);
      if (!expected || expected.userId !== userId || typeof payload.code !== "string" || startGeneration !== generation) return;
      const exchange = await send(options.direct, "/api/auth/session/direct-exchange", { method: "POST", headers, body: JSON.stringify({ code: payload.code }), signal: AbortSignal.timeout(15_000) });
      if (!exchange.ok) return;
      const verified = await send(options.direct, "/api/auth/session/transport", { signal: AbortSignal.timeout(15_000), headers: { Accept: "application/json" } });
      if (!verified.ok) return;
      if (sameSession(expected, identity(await verified.json())) && startGeneration === generation) {
        select(expected);
        if (options.directFirst) choose("direct");
      }
    })().catch(() => { /* A blocked cookie or unavailable route keeps the existing session on the proxy. */ })
      .finally(() => { pending = null; });
    return pending;
  }
  async function request(path: string, init: RequestInit) {
    const route = path.split("?")[0];
    if (["/api/auth/local/login", "/api/auth/session/exchange", "/api/auth/staging/login", "/api/dev/login-as"].includes(route)) {
      if (options.directFirst && options.direct !== options.proxy) {
        const probeGeneration = generation;
        const headers = { "X-EDUBGT-Request": "1", Accept: "application/json" };
        const probeInit = { headers, signal: init.signal || AbortSignal.timeout(15_000) };
        const start = await send(options.direct, "/api/auth/cookie-probe", { ...probeInit, method: "POST" });
        if (!start.ok) return { response: unavailableResponse(), origin: options.direct };
        const { nonce } = await start.json() as { nonce?: unknown };
        if (typeof nonce !== "string" || !/^[a-f0-9]{32}$/.test(nonce)) return { response: unavailableResponse(), origin: options.direct };
        const check = await send(options.direct, `/api/auth/cookie-probe?nonce=${nonce}`, probeInit);
        if (!check.ok) return { response: unavailableResponse(), origin: options.direct };
        const { accepted } = await check.json() as { accepted?: unknown };
        if (typeof accepted !== "boolean" || generation !== probeGeneration) return { response: unavailableResponse(), origin: options.direct };
        reset();
        choose(accepted ? "direct" : "proxy");
      } else reset();
    }
    const origin = originFor(path);
    const expected = directSession;
    const startGeneration = generation;
    const response = await send(origin, path, init);
    if (origin !== options.direct || response.status !== 401) return { response, origin };
    const recoverLegacy = options.directFirst && legacyRecovery && route === "/api/auth/me" && !expected;
    if (!expected && !recoverLegacy) return { response, origin };
    // A browser may stop sending cross-site cookies. Verify the original session before falling back.
    try {
      const fallback = await send(options.proxy, "/api/auth/session/transport", { signal: init.signal || AbortSignal.timeout(10_000), headers: { Accept: "application/json" } });
      if (fallback.status === 401) { legacyRecovery = false; return { response, origin }; }
      if (!fallback.ok) return { response: unavailableResponse(), origin };
      const fallbackIdentity = identity(await fallback.json());
      if ((!recoverLegacy && !sameSession(expected, fallbackIdentity)) || !fallbackIdentity || startGeneration !== generation) return { response, origin };
      legacyRecovery = false;
      select(null);
      if (options.directFirst) choose(recoverLegacy ? "legacy" : "proxy");
      const method = (init.method || "GET").toUpperCase();
      if (method === "GET" || method === "HEAD") return { response: await send(options.proxy, path, init), origin: options.proxy };
      // Never replay a submitted answer, upload or other write automatically.
      return { response: unavailableResponse(), origin };
    } catch {
      return { response: unavailableResponse(), origin };
    }
  }
  return { enable, request, reset, originFor };
}
